import { useRef, useEffect, useCallback, useState } from 'react';
import { Company } from '../../types/career';
import { useGameState } from './hooks/useGameState';
import { useKeyboardControls } from './hooks/useKeyboardControls';
import GameWorld from './GameWorld';
import GameControls from './GameControls';
import TutorialOverlay from './TutorialOverlay';
import BuildingDetail from './BuildingDetail';

interface StreetSceneProps {
  companies: Company[];
}

const StreetScene = ({ companies }: StreetSceneProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportWidth = 1000;
  const [showTutorial, setShowTutorial] = useState(true);
  const [speed, setSpeed] = useState(2);

  const {
    gameState,
    buildingPositions,
    worldWidth,
    moveCharacter,
    stopCharacter,
    walkToPosition,
    toggleBuildingExpansion,
    closeBuildingExpansion,
    getNearbyBuilding,
  } = useGameState({ companies, viewportWidth, speedMultiplier: speed });

  // Get nearby buildings for each side
  const nearbyTopBuildingId = getNearbyBuilding('top');
  const nearbyBottomBuildingId = getNearbyBuilding('bottom');
  // For highlighting, use any nearby building
  const nearbyBuildingId = nearbyTopBuildingId || nearbyBottomBuildingId;

  // Find the expanded company for the modal
  const expandedCompany = gameState.expandedBuilding
    ? companies.find(c => c.id === gameState.expandedBuilding)
    : null;

  const handleInteractUp = useCallback(() => {
    if (nearbyTopBuildingId) {
      toggleBuildingExpansion(nearbyTopBuildingId);
    }
  }, [nearbyTopBuildingId, toggleBuildingExpansion]);

  const handleInteractDown = useCallback(() => {
    if (nearbyBottomBuildingId) {
      toggleBuildingExpansion(nearbyBottomBuildingId);
    }
  }, [nearbyBottomBuildingId, toggleBuildingExpansion]);

  const handleClose = useCallback(() => {
    closeBuildingExpansion();
  }, [closeBuildingExpansion]);

  const handleDismissTutorial = useCallback(() => {
    setShowTutorial(false);
    containerRef.current?.focus();
  }, []);

  useKeyboardControls({
    onMoveLeft: () => moveCharacter('left'),
    onMoveRight: () => moveCharacter('right'),
    onStop: stopCharacter,
    onInteractUp: handleInteractUp,
    onInteractDown: handleInteractDown,
    onClose: handleClose,
    enabled: !showTutorial,
  });

  const handleBuildingClick = useCallback((id: string, x: number) => {
    // Walk to the building and open its info
    walkToPosition(x);
    toggleBuildingExpansion(id);
  }, [walkToPosition, toggleBuildingExpansion]);

  const handleWorldClick = useCallback((x: number) => {
    // Close any expanded building when clicking elsewhere
    closeBuildingExpansion();
    walkToPosition(x);
  }, [closeBuildingExpansion, walkToPosition]);

  // Focus container for keyboard events
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full max-w-5xl mx-auto outline-none"
      tabIndex={0}
    >
      {/* Title */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-gray-100">My Career Journey</h2>
        <p className="text-gray-600 dark:text-gray-400 text-base mt-2">
          Walk through my professional history - each building represents a chapter of my career
        </p>
      </div>

      {/* Game viewport */}
      <div
        className="relative bg-gray-900 rounded-lg overflow-hidden shadow-xl"
        style={{ width: '100%', maxWidth: `${viewportWidth}px`, margin: '0 auto' }}
      >
        <GameWorld
          companies={companies}
          buildingPositions={buildingPositions}
          gameState={gameState}
          worldWidth={worldWidth}
          viewportWidth={viewportWidth}
          nearbyBuildingId={nearbyBuildingId}
          onBuildingClick={handleBuildingClick}
          onWorldClick={handleWorldClick}
        />

        <GameControls
          onMoveLeft={() => moveCharacter('left')}
          onMoveRight={() => moveCharacter('right')}
          onStop={stopCharacter}
          showMobileControls={true}
          speed={speed}
          onSpeedChange={setSpeed}
        />

        {/* Tutorial overlay */}
        {showTutorial && (
          <TutorialOverlay onDismiss={handleDismissTutorial} />
        )}
      </div>

      {/* Tips */}
      <div className="text-center mt-6 text-base text-gray-500 dark:text-gray-400">
        <p>Click on a building to see job details, or use arrow keys to walk and explore.</p>
      </div>

      {/* Building detail modal */}
      {expandedCompany && (
        <BuildingDetail
          company={expandedCompany}
          onClose={closeBuildingExpansion}
        />
      )}
    </div>
  );
};

export default StreetScene;
